// Centralized data for all 7 marketing systems.
// Each system has: slug, name, tagline, what-it-does, how-it-works steps, hero label.

export const SYSTEMS = [
    {
        slug: "functional-website",
        name: "Functional Website",
        order: "01",
        tagline:
            "A website that actually converts visitors into booked jobs — not just a digital business card.",
        shortDescription:
            "A fast, mobile-first website built around getting calls and booked jobs. No vanity design, no fluff.",
        whatItDoes: [
            "Loads fast on mobile, where 70%+ of your traffic comes from.",
            "Makes phone numbers and Book-a-Call CTAs the focal point of every page.",
            "Hooks straight into the rest of the Elevara stack — reviews, inbox, SEO, phone.",
            "Lives on a clean, maintainable foundation so it grows with you.",
        ],
        howItWorks: [
            {
                label: "01 / Intake",
                title: "We learn your business in plain English",
                desc: "Service area, top jobs, dispatch flow, who your ideal customer is. No questionnaires longer than they need to be.",
            },
            {
                label: "02 / Build",
                title: "We build a site designed to be called from",
                desc: "Mobile-first layout, sharp copy that sounds like you (not a marketing agency), every page wired to convert.",
            },
            {
                label: "03 / Launch & Iterate",
                title: "Live in days, refined over time",
                desc: "You get a real site fast, then we tune it based on what real customers actually do on it.",
            },
        ],
    },
    {
        slug: "5-star-review-funnel",
        name: "5-Star Review Funnel",
        order: "02",
        tagline:
            "Turn every happy customer into a Google review — automatically.",
        shortDescription:
            "Automated follow-up that asks happy customers for a Google review and quietly catches unhappy ones before they post.",
        whatItDoes: [
            "Sends a review request the moment a job is marked complete.",
            "Routes happy customers to Google. Routes unhappy ones to you privately first.",
            "Stacks 5-star reviews on the listing that drives most of your local jobs.",
            "Works in the background — no chasing customers, no awkward asks.",
        ],
        howItWorks: [
            {
                label: "01 / Trigger",
                title: "Job complete → request sent",
                desc: "As soon as you close a job in your system, the funnel pings the customer with a short, human request.",
            },
            {
                label: "02 / Filter",
                title: "Happy goes public. Unhappy goes private.",
                desc: "5-star vibes get sent to your Google profile. 1–3 star feedback gets routed to you first so you can fix it before it goes live.",
            },
            {
                label: "03 / Compound",
                title: "Your review count compounds month over month",
                desc: "More reviews → better local rankings → more calls. It just runs.",
            },
        ],
    },
    {
        slug: "missed-call-text-back",
        name: "Missed Call Text-Back",
        order: "03",
        tagline: "Stop leaking jobs every time the phone rings unanswered.",
        shortDescription:
            "If you miss a call, your business automatically texts the caller back inside 30 seconds. No more lost leads.",
        whatItDoes: [
            "Detects missed calls in real time.",
            "Auto-replies with a friendly text so the caller knows you saw them.",
            "Lets you keep the conversation going from the unified inbox.",
            "Recovers the 40–60% of calls that would otherwise just go to a competitor.",
        ],
        howItWorks: [
            {
                label: "01 / Detect",
                title: "Missed call → instant trigger",
                desc: "The system watches your business line and reacts the second a call goes unanswered.",
            },
            {
                label: "02 / Text Back",
                title: "A real-sounding text hits their phone",
                desc: "In your business voice: 'Sorry we missed you — what can we help with?' Most callers reply.",
            },
            {
                label: "03 / Convert",
                title: "You take it from there in one inbox",
                desc: "The conversation lands inside your unified inbox. You book the job. Done.",
            },
        ],
    },
    {
        slug: "one-click-marketing-campaigns",
        name: "One-Click Marketing Campaigns",
        order: "04",
        tagline:
            "Pre-built email and text campaigns you can fire off in a single click.",
        shortDescription:
            "Promotions, seasonal reminders, and re-engagement campaigns ready to send to your customer list — no copywriter needed.",
        whatItDoes: [
            "Pre-built campaign templates written in your voice.",
            "Send to your full customer list or smart segments.",
            "Schedule for slow weeks, holidays, or specific seasons.",
            "Track who opened, who replied, who booked — without spreadsheets.",
        ],
        howItWorks: [
            {
                label: "01 / Pick a Campaign",
                title: "Choose what to send",
                desc: "Spring tune-up reminder? Hurricane prep blast? Win-back for cold customers? Pick from the library.",
            },
            {
                label: "02 / Click Send",
                title: "One button. Whole list.",
                desc: "Send now or schedule it. Email, text, or both. Done in under a minute.",
            },
            {
                label: "03 / Watch the Calls",
                title: "Track results without spreadsheets",
                desc: "You see who engaged, who booked, and what the campaign actually pulled in.",
            },
        ],
    },
    {
        slug: "local-seo",
        name: "Local SEO",
        order: "05",
        tagline: "Show up when your neighbors search — not on page three.",
        shortDescription:
            "Foundational local SEO that gets you discovered on Google for the services you actually want to be called for, in the areas you actually serve.",
        whatItDoes: [
            "On-page SEO tuned to the home services you sell.",
            "Local citations, listings, and schema so Google trusts your business.",
            "Optimized Google profile so your listing earns calls, not just impressions.",
            "Plays nice with the rest of the stack — reviews fuel SEO, SEO fuels calls.",
        ],
        howItWorks: [
            {
                label: "01 / Audit",
                title: "We see where you really stand",
                desc: "Real ranking data, real competitor comparisons — no PDF dump full of jargon.",
            },
            {
                label: "02 / Fix the Foundation",
                title: "On-page, listings, schema, profile",
                desc: "We clean up what's broken and build what's missing so Google actually understands your business.",
            },
            {
                label: "03 / Compound Monthly",
                title: "Steady gains, not magic spikes",
                desc: "Local SEO is a long game. We work it monthly so you keep climbing while competitors stand still.",
            },
        ],
    },
    {
        slug: "unified-inbox",
        name: "Unified Inbox",
        order: "06",
        tagline:
            "Every call, text, email, and Facebook DM — in one inbox. Stop missing things.",
        shortDescription:
            "All your customer conversations in a single inbox — phone, SMS, email, web chat, social. Reply from one place.",
        whatItDoes: [
            "Pulls every customer message into one inbox.",
            "Lets your team reply by SMS, email, or chat from one screen.",
            "Keeps a full conversation history per customer.",
            "No more 'wait, did anyone respond to that?'",
        ],
        howItWorks: [
            {
                label: "01 / Connect",
                title: "Plug in your channels",
                desc: "Phone, business email, Facebook page, web chat — connect what you use and we route it all in.",
            },
            {
                label: "02 / Reply Once",
                title: "One screen, one team, one truth",
                desc: "Reply from any device. Every team member sees the same conversation history.",
            },
            {
                label: "03 / Stop Leaking",
                title: "Nothing slips through the cracks",
                desc: "Unread states, follow-ups, missed conversations — surfaced. You stop losing jobs to your own inbox.",
            },
        ],
    },
    {
        slug: "business-phone",
        name: "Business Phone",
        order: "07",
        tagline: "A real business line that powers the rest of the stack.",
        shortDescription:
            "A dedicated business phone line that feeds calls, voicemails, and texts into your unified inbox — with call tracking baked in.",
        whatItDoes: [
            "A real business number (keep your existing one or get a new local one).",
            "Calls and SMS land in the unified inbox.",
            "Call tracking so you know which campaigns and pages actually drive calls.",
            "Voicemails get transcribed and routed where they need to go.",
        ],
        howItWorks: [
            {
                label: "01 / Provision",
                title: "Number live in 24–48 hours",
                desc: "Port your existing number or grab a new local one. Either way, you're live fast.",
            },
            {
                label: "02 / Wire It Up",
                title: "Hooked into the rest of Elevara",
                desc: "Missed call text-back, unified inbox, call tracking — it all just works because it's the same stack.",
            },
            {
                label: "03 / Run It",
                title: "Use it like a normal phone",
                desc: "Make calls, take calls, send texts. We handle the plumbing in the background.",
            },
        ],
    },
];

export const getSystemBySlug = (slug) =>
    SYSTEMS.find((s) => s.slug === slug);
